/**
 * The manual journal entry form's line state and checks, kept apart from the screen so
 * they can be tested on their own.
 */

export interface DraftLine {
  key: number
  account_id: string
  description: string
  debit: string
  credit: string
}

/** Two decimals at most, no sign -- the same shape the API's `Amount` accepts. */
const AMOUNT = /^\d{1,13}(\.\d{1,2})?$/

/**
 * An amount in minor units (paise, cents), or null when it is not an amount at all.
 *
 * The balance check runs in integers rather than on `Number(debit)`: 0.1 + 0.2 is not 0.3
 * in a double, and a form that says "out by 0.00" while refusing to post is worse than no
 * check. Blank is zero, because every line leaves one of its two columns empty.
 */
export function toMinor(amount: string): number | null {
  const trimmed = amount.trim()
  if (trimmed === '') return 0
  if (!AMOUNT.test(trimmed)) return null
  const [whole, fraction = ''] = trimmed.split('.')
  return Number(whole) * 100 + Number(fraction.padEnd(2, '0'))
}

/** What is wrong with one line, or null when the API will accept it. Mirrors `_one_side`. */
export function lineProblem(line: DraftLine): string | null {
  const debit = toMinor(line.debit)
  const credit = toMinor(line.credit)
  if (debit === null || credit === null) return 'Amounts take at most two decimals.'
  if (!line.account_id) return 'Pick an account.'
  if (debit > 0 === credit > 0) return 'Enter a debit or a credit, not both.'
  return null
}

let nextKey = 0
export const blankLine = (): DraftLine => ({
  key: (nextKey += 1),
  account_id: '',
  description: '',
  debit: '',
  credit: '',
})
